import { ChangeEvent } from 'react';
import { InputLabel, MenuItem, Select, SelectChangeEvent, TextField, FormHelperText } from '@mui/material';
import { CoreBox, CoreFormControl } from '@youscience/core';
import {
  ClassRankReporting,
  DecileRank,
  GpaWeighting,
  QuartileRank,
  QuintileRank,
  SchoolReportRequest,
  SchoolReportSchema,
} from '@youscience/commonapp-service-common';

import { TabSections } from '@interfaces/recommenderWorkspace';

import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';
import MonthYearDatePicker from '@components/MonthYearDatePicker';
import SectionAccordion from '@components/SectionAccordion';

import { dateRangeValidation, handleZodValidation } from '../../../../common/formUtils';
import ContinueButton from '@components/ContinueButton';
import { sxCommonStyles } from '../../../../common/styles';
import { FormState } from '@constants/commonApp/types';
import { StyledFieldsContainer } from '@pages/RecommenderSpace/components/Card.styles';
import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';
import { maxDate, minDate } from '@pages/RecommenderSpace/components/constants';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

export type ClassRankFields = Partial<SchoolReportRequest[TabSections.ClassRank]>;

interface ClassRankProps {
  classRank?: ClassRankFields;
  onFormChange: (formState: FormState<ClassRankFields>) => void;
  onExpand: () => void;
  onContinue: () => void;
  isExtended: boolean;
  isSaved: boolean;
}

const additionalValidations = (newFormState: FormState<ClassRankFields>) => {
  return dateRangeValidation(newFormState, {});
};

const validator = SchoolReportSchema.shape.classRank;

export const ClassRank = ({
  classRank = {},
  onFormChange,
  onExpand,
  onContinue,
  isExtended,
  isSaved,
}: ClassRankProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<ClassRankFields, typeof validator>(
    classRank,
    validator,
    isSaved,
    onFormChange,
    additionalValidations,
  );

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const handleDateChange = (fieldName: string) => (value: Date | null) => {
    if (value) {
      setFormStateWithErrors({ [fieldName]: `${value.getMonth() + 1}/${value.getFullYear()}`.padStart(7, '0') });
    } else {
      setFormStateWithErrors({ [fieldName]: undefined });
    }
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<unknown>) => {
    setFormStateWithErrors({ [field]: (event.target.value as string) || '' });
  };

  const handleTextFieldChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value && handleZodValidation(field, validator, { [field]: event.target.value }).isError) return;
    setFormStateWithErrors({ [field]: event.target.value || undefined });
  };

  const handleRadioChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormStateWithErrors({ [field]: event.target.value });
  };

  return (
    <SectionAccordion
      title="Class Rank"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={isShowErrors && !formState.isFormValid}
    >
      <StyledFieldsContainer>
        <CoreBox sx={sxCommonStyles.fieldsWrapper}>
          <CoreFormControl fullWidth error={!!formState.errors.reporting}>
            <InputLabel required>How do you report class rank?</InputLabel>

            <Select
              label="How do you report class rank? *"
              value={formState.data.reporting ?? ''}
              onChange={handleSelectChange('reporting')}
            >
              {Object.entries(ClassRankReporting).map(([key, value]) => {
                return (
                  <MenuItem key={value} value={value}>
                    {key}
                  </MenuItem>
                );
              })}
            </Select>
            {formState.errors.reporting && <FormHelperText>{formState.errors.reporting}</FormHelperText>}
          </CoreFormControl>

          {formState.data.reporting === ClassRankReporting.Exact && (
            <CoreFormControl fullWidth>
              <TextField
                name="exact"
                label="Exact rank"
                value={formState.data.exact ?? ''}
                required
                error={!!formState.errors.exact}
                helperText={formState.errors.exact}
                onChange={handleTextFieldChange('exact')}
              />
            </CoreFormControl>
          )}

          {formState.data.reporting === ClassRankReporting.Decile && (
            <CoreFormControl fullWidth error={!!formState.errors.decile}>
              <InputLabel required>Decile Rank</InputLabel>

              <Select
                label="Decile rank? *"
                value={formState.data.decile ?? ''}
                onChange={handleSelectChange('decile')}
              >
                {Object.entries(DecileRank).map(([key, value]) => {
                  return (
                    <MenuItem key={value} value={value}>
                      {key}
                    </MenuItem>
                  );
                })}
              </Select>
              {formState.errors.decile && <FormHelperText>{formState.errors.decile}</FormHelperText>}
            </CoreFormControl>
          )}

          {formState.data.reporting === ClassRankReporting.Quartile && (
            <CoreFormControl fullWidth error={!!formState.errors.quartile}>
              <InputLabel required>Quartile Rank</InputLabel>

              <Select
                label="Quartile rank? *"
                value={formState.data.quartile ?? ''}
                onChange={handleSelectChange('quartile')}
              >
                {Object.entries(QuartileRank).map(([key, value]) => {
                  return (
                    <MenuItem key={value} value={value}>
                      {key}
                    </MenuItem>
                  );
                })}
              </Select>
              {formState.errors.quartile && <FormHelperText>{formState.errors.quartile}</FormHelperText>}
            </CoreFormControl>
          )}

          {formState.data.reporting === ClassRankReporting.Quintile && (
            <CoreFormControl fullWidth error={!!formState.errors.quintile}>
              <InputLabel required>Quintile Rank</InputLabel>

              <Select
                label="Quintile rank? *"
                value={formState.data.quintile ?? ''}
                onChange={handleSelectChange('quintile')}
              >
                {Object.entries(QuintileRank).map(([key, value]) => {
                  return (
                    <MenuItem key={value} value={value}>
                      {key}
                    </MenuItem>
                  );
                })}
              </Select>
              {formState.errors.quintile && <FormHelperText>{formState.errors.quintile}</FormHelperText>}
            </CoreFormControl>
          )}
        </CoreBox>

        {formState.data.reporting === ClassRankReporting.Exact && (
          <CoreFormControl fullWidth>
            <TextField
              name="sharing"
              label="How many additional students share this rank?"
              value={formState.data.sharing ?? ''}
              required
              error={!!formState.errors.sharing}
              helperText={formState.errors.sharing}
              onChange={handleTextFieldChange('sharing')}
            />
          </CoreFormControl>
        )}

        {formState.data.reporting !== undefined && formState.data.reporting !== ClassRankReporting.None && (
          <CoreBox sx={{ display: 'flex', gap: '1.5rem', flexDirection: 'column' }}>
            <RadioButtonGroupWithClearButton
              name="weighting"
              title="Rank Weighting"
              value={formState.data.weighting ?? undefined}
              options={GpaWeighting}
              required
              handleRadioChange={handleRadioChange}
              handleClear={() => setFormStateWithErrors({ weighting: undefined })}
              error={!!formState.errors.weighting}
              helperText={formState.errors.weighting}
            />

            <CoreBox sx={sxCommonStyles.fieldsWrapper}>
              <MonthYearDatePicker
                name="startDate"
                label="From (start date)"
                required
                slotProps={{
                  inputProps: { clearable: true, onClear: () => handleDateChange('startDate') },
                  error: !formState.data.startDate || Boolean(formState.errors['startDate']),
                }}
                value={formState.data.startDate ?? undefined}
                error={!formState.data.startDate ? REQUIRED_FIELD_MESSAGE : formState.errors.startDate}
                handleDateChange={handleDateChange('startDate')}
                minDate={minDate}
                maxDate={maxDate}
              />

              <MonthYearDatePicker
                name="endDate"
                label="Ending date (final date)"
                required
                slotProps={{
                  inputProps: { clearable: true, onClear: () => handleDateChange('endDate') },
                  error: !formState.data.endDate || Boolean(formState.errors['endDate']),
                }}
                value={formState.data.endDate ?? undefined}
                error={!formState.data.endDate ? REQUIRED_FIELD_MESSAGE : formState.errors.endDate}
                handleDateChange={handleDateChange('endDate')}
                minDate={minDate}
                maxDate={maxDate}
              />
            </CoreBox>
          </CoreBox>
        )}

        <ContinueButton onContinue={onContinue} />
      </StyledFieldsContainer>
    </SectionAccordion>
  );
};
